import { useRef, useCallback } from 'react';

const SERVICE_TAG = '[SWIPE_GESTURE]';

/**
 * Swipe direction options
 */
export type SwipeDirection = 'up' | 'down' | 'left' | 'right';

/**
 * Options for swipe gesture detection
 */
export interface SwipeGestureOptions {
  /**
   * Direction to detect swipe
   */
  direction: SwipeDirection;
  /**
   * Minimum distance in pixels to trigger swipe (default: 50)
   */
  threshold?: number;
  /**
   * Maximum duration in ms for valid swipe (default: 500)
   */
  maxDuration?: number;
  /**
   * Callback when swipe detected
   */
  onSwipe: () => void;
}

/**
 * State tracked during gesture
 */
interface GestureState {
  isTracking: boolean;
  startX: number;
  startY: number;
  startTime: number;
  pointerId: number | null;
  wasSwiped: boolean;
}

/**
 * Hook for detecting swipe gestures using Pointer Events API.
 * Follows codebase pattern from FloatingCaptureButton.
 *
 * @param options - Swipe configuration
 * @returns Event handlers to attach to target element
 *
 * @example
 * const swipeHandlers = useSwipeGesture({
 *   direction: 'up',
 *   threshold: 50,
 *   onSwipe: () => console.log('Swiped up!')
 * });
 *
 * <div {...swipeHandlers}>Swipe me</div>
 */
export function useSwipeGesture(options: SwipeGestureOptions) {
  const { direction, threshold = 50, maxDuration = 500, onSwipe } = options;

  const gestureState = useRef<GestureState>({
    isTracking: false,
    startX: 0,
    startY: 0,
    startTime: 0,
    pointerId: null,
    wasSwiped: false
  });

  /**
   * Check if movement matches expected swipe direction
   */
  const isValidSwipe = useCallback(
    (deltaX: number, deltaY: number): boolean => {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      switch (direction) {
        case 'up':
          return deltaY < -threshold && absY > absX;
        case 'down':
          return deltaY > threshold && absY > absX;
        case 'left':
          return deltaX < -threshold && absX > absY;
        case 'right':
          return deltaX > threshold && absX > absY;
        default:
          console.warn(`${SERVICE_TAG} Unknown direction, using fallback`, {
            direction,
            fallbackValue: false
          });
          return false;
      }
    },
    [direction, threshold]
  );

  /**
   * Reset gesture state, optionally preserving wasSwiped flag
   * @param preserveSwipeFlag - If true, keeps the wasSwiped value for click handler coordination
   */
  const resetGesture = useCallback((preserveSwipeFlag = false) => {
    const wasSwiped = preserveSwipeFlag ? gestureState.current.wasSwiped : false;
    gestureState.current = {
      isTracking: false,
      startX: 0,
      startY: 0,
      startTime: 0,
      pointerId: null,
      wasSwiped
    };
  }, []);

  /**
   * Handle pointer down - start tracking gesture.
   * No pointer capture is used — it redirects all subsequent pointer events
   * (including pointerup and click) to the capturing element, which breaks
   * mouse clicks and onPointerUp handlers on child elements (buttons, links).
   * Since the swipe container is typically full-viewport, events bubble up
   * naturally without needing capture.
   */
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    gestureState.current = {
      isTracking: true,
      startX: e.clientX,
      startY: e.clientY,
      startTime: Date.now(),
      pointerId: e.pointerId,
      wasSwiped: false
    };
  }, []);

  /**
   * Handle pointer up - check if gesture is valid swipe.
   * Releases pointer capture and stops propagation if swipe detected
   * to prevent the event from triggering click handlers on parent elements.
   */
  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!gestureState.current.isTracking) {
        return;
      }

      const { startX, startY, startTime } = gestureState.current;
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      const duration = Date.now() - startTime;

      // Ignore slow drags
      if (duration > maxDuration) {
        console.debug(
          `${SERVICE_TAG} Gesture too slow (${duration}ms > ${maxDuration}ms)`
        );
        resetGesture();
        return;
      }

      // Check if movement matches expected direction
      if (isValidSwipe(deltaX, deltaY)) {
        console.debug(`${SERVICE_TAG} Valid swipe detected`, {
          direction,
          deltaX,
          deltaY,
          duration
        });

        // Mark that a swipe occurred - this prevents click handler from firing
        gestureState.current.wasSwiped = true;

        // Stop propagation to prevent this event from triggering menu clicks
        e.stopPropagation();

        onSwipe();

        // Reset but preserve wasSwiped flag briefly for onClick coordination
        resetGesture(true);

        // Clear wasSwiped flag after a short delay to allow click event to check it
        setTimeout(() => {
          gestureState.current.wasSwiped = false;
        }, 100);
      } else {
        resetGesture();
      }
    },
    [direction, maxDuration, isValidSwipe, onSwipe, resetGesture]
  );

  /**
   * Handle pointer cancel - reset gesture state
   */
  const onPointerCancel = useCallback(() => {
    resetGesture();
  }, [resetGesture]);

  /**
   * Check if the last gesture was a swipe (for click handler coordination).
   * Call this from onClick to determine if the click should be ignored.
   * @returns true if a swipe was just detected
   */
  const wasSwipe = useCallback(() => {
    return gestureState.current.wasSwiped;
  }, []);

  return {
    onPointerDown,
    onPointerUp,
    onPointerCancel,
    wasSwipe
  };
}
